import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Activity } from '../../activities/activity.model';
import { ActivitiesService } from '../../activities/activities.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators';
import { TakerService } from '../../shared/taker.service';
import { ActivityCardComponent } from 'src/app/shared/activity-card/activity-card.component';

@Component({
  selector: 'app-taker-favorites',
  standalone: true,
  imports: [InfiniteScrollModule, ActivityCardComponent, CommonModule],
  templateUrl: './taker-favorites.component.html',
  styleUrls: ['./taker-favorites.component.scss'],
})
export class TakerFavoritesComponent implements OnInit {
  // UI state
  public expandedIndex = -1;
  public loading = false;

  // Activity data
  public originalActivities: Activity[] = [];
  public activities: Activity[] = [];

  public toggleFav = false;

  // Infinite scroll configuration
  private readonly ITEMS_PER_PAGE = 9;
  public throttle = 150;
  public scrollDistance = 0.1;
  public canLoadMore = true;
  notEmptyActivity = true;
  notScrolly = true;

  constructor(
    private activityService: ActivitiesService,
    private takerService: TakerService,
    private router: Router,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    this.loadInitialActivities();
  }

  private loadInitialActivities(): void {
    this.loading = true;

    this.activityService
      .getActivitiesByTakerId(this.takerService.getId())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.originalActivities = response['hydra:member'] || [];
          // Load initial batch
          this.activities = this.originalActivities.slice(
            0,
            this.ITEMS_PER_PAGE
          );
          this.canLoadMore =
            this.activities.length < this.originalActivities.length;
        },
        error: (error) => {
          console.error('Error loading favorite activities:', error);
          // Handle error appropriately - maybe show a user-friendly message
          if (this.notScrolly && this.notEmptyActivity) {
            this.notScrolly = false;
            setTimeout(() => {
              this.loadNextActivity();
            }, 500);
          } else {
            this.loading = false;
          }
        },
      });
  }

  private loadNextActivity() {
    if (this.activities.length < this.originalActivities.length) {
      let i;
      const length = this.activities.length;
      const activityPerPage = 9;
      const activityToLoad = this.originalActivities.length - length;

      if (activityToLoad >= activityPerPage) {
        for (i = length; i <= length + activityPerPage; i++) {
          this.activities.push(this.originalActivities[i]);
        }
      } else {
        for (i = length; i <= length + activityToLoad - 1; i++) {
          this.activities.push(this.originalActivities[i]);
        }
      }
    }
  }

  public clickFavorite(event: Event): void {
    this.toggleFav = !this.toggleFav;
  }

  public expandText(index: number): void {
    this.expandedIndex = index === this.expandedIndex ? -1 : index;
  }

  public onScroll(): void {
    if (!this.canLoadMore || this.loading) {
      return;
    }

    this.loading = true;
    // Use setTimeout to prevent rapid multiple loads
    setTimeout(() => {
      this.loadNextBatch();
    }, 200);
  }

  private loadNextBatch(): void {
    const currentLength = this.activities.length;
    const remainingItems = this.originalActivities.length - currentLength;

    if (remainingItems <= 0) {
      this.canLoadMore = false;
      this.loading = false;
      return;
    }

    const itemsToLoad = Math.min(this.ITEMS_PER_PAGE, remainingItems);
    const newActivities = this.originalActivities.slice(
      currentLength,
      currentLength + itemsToLoad
    );

    this.activities = [...this.activities, ...newActivities];
    this.canLoadMore = this.activities.length < this.originalActivities.length;
    this.loading = false;
  }

  public getActivityTrackBy(_index: number, item: Activity): string | number {
    return item.id; // Now accepts either string or number type
  }
}
